import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import EastIcon from '@mui/icons-material/East';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DeleteIcon from '@mui/icons-material/Delete';
import { selectPendingChanges, clearPendingChanges, removeOperationFromPending, selectPatches } from '../patchesSlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

interface OperationValue {
  name?: string;
  internalName?: string;
  id?: string;
  [key: string]: any;
}

interface Operation {
  __typename: string;
  [key: string]: string | number | boolean | OperationValue | null;
}

interface Patch {
  patchId: string;
  storeNumber: string;
  [key: string]: any;
}

interface PendingChangesViewProps {
  open: boolean;
  onClose: () => void;
}

const OperationTreeItem: React.FC<{ 
  operation: Operation; 
  nodeId: string; 
  index: number; 
  operationType: 'set' | 'remove';
  onDeleteOperation: (operationType: 'set' | 'remove', index: number) => void;
  patches: Patch[];
  patchIds: string[];
}> = ({
  operation,
  nodeId,
  index,
  operationType,
  onDeleteOperation,
  patches,
  patchIds,
}) => {
  // Operation type constants
  const OPERATION_TYPES = {
    SET_VARIANT_PRICE: 'SetVariantPriceMenuPatchOperation',
    SET_BUNDLE_PRICE: 'SetBundlePriceMenuPatchOperation',
    SET_MODIFIER_WEIGHT_PRICE: 'SetModifierWeightPriceMenuPatchOperation',
    SET_MODIFIER_PRICE_RANGES: 'SetModifierPriceRangesMenuPatchOperation',
    REMOVE_PRODUCT: 'RemoveProductMenuPatchOperation',
    REMOVE_VARIANT: 'RemoveVariantMenuPatchOperation',
    REMOVE_BUNDLE: 'RemoveBundleMenuPatchOperation',
    REMOVE_MODIFIER_FROM_SLOT: 'RemoveModifierFromSlotPatchOperation',
    REMOVE_MODIFIER: 'RemoveModifierMenuPatchOperation',
    SET_MODIFIER_WEIGHT_EXCLUDED: 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation'
  } as const;

  // Get store numbers for this specific operation
  const getOperationStores = () => {
    // If the operation has its own affectedStores array (from deduplication), use that
    if (operation.affectedStores && Array.isArray(operation.affectedStores)) {
      return operation.affectedStores;
    }
    // Fallback to the previous logic for operations without affectedStores
    const storeNumbers: string[] = [];
    patchIds.forEach(patchId => {
      const patch = patches.find(p => p.patchId === patchId);
      if (patch?.storeNumber && !storeNumbers.includes(patch.storeNumber)) {
        storeNumbers.push(patch.storeNumber);
      }
    });
    return storeNumbers.sort();
  };

  const operationStores = getOperationStores();

  // Helper to get display label for a bundle
  const getBundleLabel = (bundle: any) => {
    if (!bundle) return 'Unknown Bundle';
    if (bundle.displayName) return bundle.displayName;
    if (bundle.internalName && bundle.bundleCode) return `${bundle.internalName} (${bundle.bundleCode})`;
    if (bundle.name && bundle.bundleCode) return `${bundle.name} (${bundle.bundleCode})`;
    return bundle.name || bundle.internalName || 'Unknown Bundle';
  };

  // Helper to get operation action description for all operations
  const getOperationActionDescription = (operationType: string, isRemoveOp: boolean) => {
    if (isRemoveOp) {
      // Remove operations - show what's being removed
      switch (operationType) {
        // SetProdPrices operations in remove array
        case OPERATION_TYPES.SET_VARIANT_PRICE:
          return 'remove variant price';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_PRICE:
          return 'remove modifier weight price';
        case OPERATION_TYPES.SET_MODIFIER_PRICE_RANGES:
          return 'remove modifier price ranges';
        
        // SetBundPrice operations in remove array  
        case OPERATION_TYPES.SET_BUNDLE_PRICE:
          return 'remove bundle price';
        
        // RmvProd operations in remove array (removing exclusions)
        case OPERATION_TYPES.REMOVE_PRODUCT:
          return 'remove product exclusion';
        case OPERATION_TYPES.REMOVE_VARIANT:
          return 'remove variant exclusion';
        case OPERATION_TYPES.REMOVE_MODIFIER_FROM_SLOT:
          return 'remove modifier exclusion from slot';
        case OPERATION_TYPES.REMOVE_MODIFIER:
          return 'remove modifier exclusion';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_EXCLUDED:
          return 'remove modifier weight exclusion from variant';
        
        // RmvBund operations in remove array (removing exclusions)
        case OPERATION_TYPES.REMOVE_BUNDLE:
          return 'remove bundle exclusion';
        
        default:
          return 'remove operation';
      }
    } else {
      // Set operations - show what's being set/added
      switch (operationType) {
        // SetProdPrices operations in set array (setting prices)
        case OPERATION_TYPES.SET_VARIANT_PRICE:
          return 'set variant price';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_PRICE:
          return 'set modifier weight price';
        case OPERATION_TYPES.SET_MODIFIER_PRICE_RANGES:
          return 'set modifier price ranges';
        
        // SetBundPrice operations in set array (setting prices)
        case OPERATION_TYPES.SET_BUNDLE_PRICE:
          return 'set bundle price';
        
        // RmvProd operations in set array (adding exclusions)
        case OPERATION_TYPES.REMOVE_PRODUCT:
          return 'add product exclusion';
        case OPERATION_TYPES.REMOVE_VARIANT:
          return 'add variant exclusion';
        case OPERATION_TYPES.REMOVE_MODIFIER_FROM_SLOT:
          return 'add modifier exclusion from slot';
        case OPERATION_TYPES.REMOVE_MODIFIER:
          return 'add modifier exclusion';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_EXCLUDED:
          return 'add modifier weight exclusion to variant';
        
        // RmvBund operations in set array (adding exclusions)
        case OPERATION_TYPES.REMOVE_BUNDLE:
          return 'add bundle exclusion';
        
        default:
          return 'set operation';
      }
    }
  };

  const renderOperationDetails = (op: Operation) => {
    const details = [];
    
    // Helper function to get the primary property key for each operation type
    const getPrimaryPropertyKey = (operationType: string): string | null => {
      switch (operationType) {
        case OPERATION_TYPES.SET_VARIANT_PRICE:
          return 'setVariantPrice';
        case OPERATION_TYPES.SET_BUNDLE_PRICE:
          return 'setBundlePrice';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_PRICE:
          return 'setModifierWeightPrice';
        case OPERATION_TYPES.SET_MODIFIER_PRICE_RANGES:
          return 'setModifierPriceRanges';
        case OPERATION_TYPES.REMOVE_PRODUCT:
          return 'removeProduct';
        case OPERATION_TYPES.REMOVE_VARIANT:
          return 'removeVariant';
        case OPERATION_TYPES.REMOVE_BUNDLE:
          return 'removeBundle';
        case OPERATION_TYPES.REMOVE_MODIFIER_FROM_SLOT:
          return 'removeModifierFromSlot';
        case OPERATION_TYPES.REMOVE_MODIFIER:
          return 'removeModifier';
        case OPERATION_TYPES.SET_MODIFIER_WEIGHT_EXCLUDED:
          return 'setModifierWeightIsExcludedFromVariant';
        default:
          return null;
      }
    };

    const productDetails = (
      typedValue: OperationValue,
      authoringPrice: string,
      key: string,
      index: number,
      altBody: boolean,
      operationType: 'set' | 'remove'
    ) => {
      // Safety check - ensure typedValue is an object
      if (!typedValue || typeof typedValue !== 'object') {
        return null;
      }

      const isExclusionOp = [
        OPERATION_TYPES.REMOVE_BUNDLE,
        OPERATION_TYPES.REMOVE_PRODUCT,
        OPERATION_TYPES.REMOVE_VARIANT,
        OPERATION_TYPES.REMOVE_MODIFIER_FROM_SLOT,
        OPERATION_TYPES.REMOVE_MODIFIER,
        OPERATION_TYPES.SET_MODIFIER_WEIGHT_EXCLUDED
      ].includes(op.__typename as any);

      const isProduct = !!typedValue.product;
      const isBundle = !!typedValue.bundle;
      const isVariant = !!typedValue.variant;
      const isModifier = !!typedValue.modifier && typedValue.__typename !== 'RemoveModifierFromSlotPatchOperation';
      const isSlot = !!typedValue.slot;

      let mainText = 'Unknown Item';
      
      if (isProduct) {
        mainText = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
      } else if (isBundle) {
        mainText = getBundleLabel(typedValue.bundle);
      } else if (isVariant) {
        // For variant operations (like setVariantPrice), show product-variant combination
        const productName = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
        let variantName = '';
        if (Array.isArray(typedValue.variant?.selectedOptions) && typedValue.variant.selectedOptions.length > 0) {
          variantName = typedValue.variant.selectedOptions.map((so: any) => so?.optionValue?.internalName || so?.optionValue?.name || '').filter(Boolean).join(', ') || 'Variant';
        } else {
          variantName = typedValue.variant?.name || typedValue.variant?.internalName || 'Base Variant';
        }
        mainText = `${productName} - ${variantName}`;
      } else if (isModifier) {
        mainText = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
      } else if (isSlot) {
        mainText = typedValue.slot?.name || typedValue.slot?.internalName || typedValue.slot?.slotCode || typedValue.slotCode || 'Unknown Slot';
      }

      // Special handling for RemoveModifierFromSlotPatchOperation - show slot name as main text
      if (op.__typename === 'RemoveModifierFromSlotPatchOperation') {
        const slotName = typedValue.slot?.name || typedValue.slot?.internalName || 'Unknown Slot';
        mainText = slotName;
      }

      // Special handling for RemoveModifierMenuPatchOperation - show modifier info
      if (op.__typename === 'RemoveModifierMenuPatchOperation') {
        const modifierName = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
        mainText = modifierName;
      }

      // Special handling for SetModifierWeightIsExcludedFromVariantMenuPatchOperation - show just product name
      if (op.__typename === 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation') {
        const productName = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
        mainText = productName;
      }
      const bundleStyle = isBundle ? { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' } : {};
      return (
        <Box sx={{ ...bundleStyle }}>
          <Typography component="div">{mainText}</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              {bodyDetails(typedValue, altBody, isProduct)}
              {weightDetails(typedValue, key, index, operationType)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Show operation action description for all operations based on type */}
              {(() => {
                const description = getOperationActionDescription(op.__typename, operationType === 'remove');
                const isRemoveOperation = operationType === 'remove';

                // For pricing operations in set mode, integrate price into description
                if (operationType === 'set' && !typedValue.weightPriceRanges && !!authoringPrice && !isExclusionOp) {
                  return description ? (
                    <Typography component="div" sx={{
                      color: 'inherit',
                      fontWeight: 'normal'
                    }}>
                      {description} <EastIcon fontSize='small' sx={{ mb: -0.75 }} /> {authoringPrice}
                    </Typography>
                  ) : null;
                }

                // For all other operations, show description only
                return description ? (
                  <Typography component="div" sx={{ 
                    color: isRemoveOperation ? 'error.main' : 'inherit', 
                    fontWeight: isRemoveOperation ? 'medium' : 'normal'
                  }}>
                    {description}
                  </Typography>
                ) : null;
              })()}
              {isExcludedDetails(typedValue)}
            </Box>
          </Box>
        </Box>
      );
    };

    const bodyDetails = (typedValue: OperationValue, altBody: boolean, isProduct: boolean) => {
      // Special handling for SetModifierWeightIsExcludedFromVariantMenuPatchOperation
      if (op.__typename === OPERATION_TYPES.SET_MODIFIER_WEIGHT_EXCLUDED) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {variantDetails(typedValue)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {slotDetails(typedValue)}
              {modifierDetails(typedValue)}
              {singleWeightDetails(typedValue)}
            </Box>
          </Box>
        );
      }

      if (!isProduct) {
        if (op.__typename === 'RemoveModifierFromSlotPatchOperation') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {modifierDetails(typedValue)}
            </Box>
          );
        }
        if (op.__typename === 'RemoveModifierMenuPatchOperation') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {modifierDetails(typedValue)}
            </Box>
          );
        }
        return null;
      }
      return !altBody ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {typedValue.variant && <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />}
          {variantDetails(typedValue)}
          {slotDetails(typedValue)}
          {modifierDetails(typedValue)}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
            {variantDetails(typedValue)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {slotDetails(typedValue)}
            {modifierDetails(typedValue)}
          </Box>
        </Box>
      );
    };

    const variantDetails = (typedValue: OperationValue) => {
      if (!typedValue.variant) return null;
      // Build label from product and variant option values
      const productName = typedValue.product?.name || typedValue.product?.internalName || '';
      let variantLabel = '';
      if (Array.isArray(typedValue.variant?.selectedOptions) && typedValue.variant.selectedOptions.length > 0) {
        variantLabel = typedValue.variant.selectedOptions.map((so: any) => so?.optionValue?.internalName || so?.optionValue?.name || '').filter(Boolean).join(', ') || 'Variant';
      } else {
        variantLabel = typedValue.variant?.name || typedValue.variant?.internalName || 'Base Variant';
      }
      const label = productName ? `${productName} - ${variantLabel}` : variantLabel;
      return (
        <Box>
          <Typography variant='body2'>Variant</Typography>
          <Chip label={label} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const slotDetails = (typedValue: OperationValue) => {
      if (!typedValue.slot) return null;
      const label = typedValue.slot?.name || typedValue.slot?.internalName || 'Unknown Slot';
      return (
        <Box>
          <Typography variant='body2'>Slot</Typography>
          <Chip label={label} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const modifierDetails = (typedValue: OperationValue) => {
      if (!typedValue.modifier) return null;
      const modifierName = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
      return (
        <Box>
          <Typography variant='body2'>Modifier</Typography>
          <Chip label={modifierName} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const weightDetails = (typedValue: OperationValue, key: string, index: number, operationType: 'set' | 'remove') => {
      if (!typedValue.weightPriceRanges) return null;
      return (
        <Box>
          {typedValue.weightPriceRanges.map((weightPriceRange: any, rangeIdx: number) => (
            <Typography
              key={`range-${weightPriceRange.rangeStart}-${weightPriceRange.rangeEnd}-${index}-${rangeIdx}`}
            >
              Range {weightPriceRange.rangeStart} to{' '}
              {weightPriceRange.rangeEnd ? (
                weightPriceRange.rangeEnd
              ) : (
                <AllInclusiveIcon fontSize='small' sx={{ mb: -0.75, ml: 0.5 }} />
              )}{' '}
              {/* Show integrated pricing for set operations only */}
              {operationType === 'set' && weightPriceRange.authoringPrice?.amount && (
                <>
                  <EastIcon fontSize='small' sx={{ mb: -0.75 }} /> $
                  {weightPriceRange.authoringPrice.amount}
                </>
              )}
            </Typography>
          ))}
        </Box>
      );
    };

    const singleWeightDetails = (typedValue: OperationValue) => {
      if (!typedValue.weight) return null;
      const weightName = typedValue.weight?.name || typedValue.weight?.internalName || 'Unknown Weight';
      return (
        <Box>
          <Typography variant='body2'>Weight</Typography>
          <Chip label={weightName} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const isExcludedDetails = (typedValue: OperationValue) => {
      if (!typedValue || typeof typedValue !== 'object' || !('isExcluded' in typedValue)) return null;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>Excluded: </Typography>
          <Chip label={typedValue.weight?.name || 'Unknown'} sx={{ ml: 1 }} />
        </Box>
      );
    };

    // Get the primary property key for this operation type
    const primaryKey = getPrimaryPropertyKey(op.__typename);
    
    if (primaryKey && op[primaryKey]) {
      // Only process the primary property to avoid duplication
      const value = op[primaryKey];
      if (value && typeof value === 'object') {
        const typedValue = value as OperationValue;
        const authoringPrice = typedValue.authoringPrice ? `$${typedValue.authoringPrice.amount}` : '';
        details.push(
          <React.Fragment key={`${primaryKey}-0`}>
            {productDetails(
              typedValue,
              authoringPrice,
              primaryKey,
              0,
              primaryKey === 'setModifierWeightPrice' || primaryKey === 'setModifierWeightIsExcludedFromVariant' ? true : false,
              operationType
            )}
          </React.Fragment>
        );
      }
    } else {
      // If we don't have a primary key mapping, show a warning rather than falling back
      console.warn(`Unknown operation type: ${op.__typename}. Please add mapping to getPrimaryPropertyKey.`);
      details.push(
        <React.Fragment key="unknown-operation">
          <Typography color="error">Unknown operation type: {op.__typename}</Typography>
        </React.Fragment>
      );
    }
    return <>{details}</>;
  };

  return (
    <TreeItem
      nodeId={nodeId}
      label={
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant='body2' component="div" sx={{ fontWeight: 'medium', width: '100%' }}>
              {renderOperationDetails(operation)}
            </Typography>
            {/* Store information per operation */}
            {operationStores.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant='caption' color='text.secondary'>
                  Stores ({operationStores.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                  {operationStores.slice(0, 5).map(storeNumber => (
                    <Chip 
                      key={storeNumber} 
                      label={storeNumber} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: '16px',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  ))}
                  {operationStores.length > 5 && (
                    <Chip 
                      label={`+${operationStores.length - 5}`}
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: '16px',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteOperation(operationType, index);
            }}
            sx={{ ml: 1 }}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Box>
      }
    />
  );
};

const PendingChangesView: React.FC<PendingChangesViewProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const pendingChange = useSelector(selectPendingChanges);
  const patches = useSelector(selectPatches);
  const [expanded, setExpanded] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);

  // Toggle expansion of the change
  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // Handle Clear All with confirmation
  const handleClearAllClick = () => {
    setShowClearAllModal(true);
  };

  const handleCancelClearAll = () => {
    setShowClearAllModal(false);
  };

  // Handle individual operation deletion
  const handleDeleteOperation = (operationType: 'set' | 'remove', operationIndex: number) => {
    dispatch(removeOperationFromPending({ operationType, operationIndex }));
  };

  if (!pendingChange.id) {
    return null;
  }

  return (
    <Paper variant='outlined'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h6' sx={{ mr: 1 }}>
            Pending Change(s)
          </Typography>
          <Badge
            badgeContent={pendingChange.data.setOperations.length + pendingChange.data.removeOperations.length}
            color='primary'
            sx={{ mr: 1 }}
          >
            <InfoIcon fontSize='small' />
          </Badge>
          <Tooltip title='This change will be applied when you submit the patch'>
            <Typography variant='body2' color='text.secondary'>
              unsaved
            </Typography>
          </Tooltip>
        </Box>
        <Box>
          <Button 
            variant='outlined' 
            color='error' 
            size='small' 
            onClick={handleClearAllClick} 
            sx={{ mr: 1 }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
      <Paper variant='outlined'>
        <List dense>
          <ListItem
            button
            onClick={handleToggleExpand}
            sx={{
              backgroundColor: expanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              p: 1,
            }}
          >
            <ListItemText
              primary={pendingChange.type}
              secondary={
                <Typography variant="caption" display="block">
                  Set Operations: {pendingChange.data.setOperations.length}, Remove Operations: {pendingChange.data.removeOperations.length}
                </Typography>
              }
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 'medium',
              }}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge='end'
                aria-label='expand'
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand();
                }}
                size='small'
              >
                {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant='caption' display='block' gutterBottom>
                Change Details:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: '400px',
                }}
              >
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  sx={{
                    height: 'auto',
                    minHeight: 200,
                    maxHeight: 400,
                    flexGrow: 1,
                    maxWidth: '100%',
                    overflowY: 'auto',
                  }}
                >
                  <TreeItem
                    nodeId='set-operations'
                    label={
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        Set Operations ({pendingChange.data.setOperations.length})
                      </Typography>
                    }
                  >
                    {pendingChange.data.setOperations.map((opObj, index) => {
                      // Type guard for new and legacy formats
                      let operation: any = opObj;
                      if (opObj && typeof opObj === 'object') {
                        if ('enriched' in opObj && opObj.enriched && opObj.enriched.__typename) {
                          operation = opObj.enriched;
                        } else if ('minimal' in opObj && opObj.minimal && opObj.minimal.__typename) {
                          operation = opObj.minimal;
                        }
                      }
                      return (
                        <OperationTreeItem
                          key={`set-${index}`}
                          nodeId={`set-op-${index}`}
                          operation={operation}
                          index={index}
                          operationType="set"
                          onDeleteOperation={handleDeleteOperation}
                          patches={patches}
                          patchIds={pendingChange.data.patchIds}
                        />
                      );
                    })}
                  </TreeItem>
                  <TreeItem
                    nodeId='remove-operations'
                    label={
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        Remove Operations ({pendingChange.data.removeOperations.length})
                      </Typography>
                    }
                  >
                    {pendingChange.data.removeOperations.map((opObj, index) => {
                      let operation: any = opObj;
                      if (opObj && typeof opObj === 'object') {
                        if ('enriched' in opObj && opObj.enriched && opObj.enriched.__typename) {
                          operation = opObj.enriched;
                        } else if ('minimal' in opObj && opObj.minimal && opObj.minimal.__typename) {
                          operation = opObj.minimal;
                        }
                      }
                      return (
                        <OperationTreeItem
                          key={`remove-${index}`}
                          nodeId={`remove-op-${index}`}
                          operation={operation}
                          index={index}
                          operationType="remove"
                          onDeleteOperation={handleDeleteOperation}
                          patches={patches}
                          patchIds={pendingChange.data.patchIds}
                        />
                      );
                    })}
                  </TreeItem>
                </TreeView>
              </Box>
            </Box>
          </Collapse>
        </List>
      </Paper>
      {/* Clear All Confirmation Modal */}
      <Dialog open={showClearAllModal} onClose={handleCancelClearAll}>
        <DialogTitle>Clear All Pending Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all pending changes? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClearAll} color="primary">
            Cancel
          </Button>
          <Button onClick={() => { dispatch(clearPendingChanges()); setShowClearAllModal(false); }} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PendingChangesView;